
/**
 * Module dependencies.
 */

import { ImageSource, Payspace, PayspaceSignupMode, PayspaceStatus } from '../types';
import { ImageSources } from '../design-system';
import { PayspaceNotFound } from './payspace-not-found';
import { isEmpty } from 'lodash';
import { parseObject } from '../core';
import React, { ReactNode, createContext, useCallback, useContext, useState } from 'react';

/**
 * Initial payspace data.
 */

let initialPayspaceData: Payspace | null = null;

try {
  const rawPayspace = (window as any)._payspace; // eslint-disable-line no-underscore-dangle

  if (rawPayspace) {
    initialPayspaceData = parseObject(rawPayspace) ?? {};
  }
} catch (error) {
  // Ignore, the Payspace Not Found page will be rendered.
}

/**
 * Payspace context.
 */

// This should only be used inside a subdomain,
// so Payspace should never be null when this context is used.
const PayspaceContext = createContext<Payspace | null>(initialPayspaceData);

/**
 * Export `usePayspace`.
 */

export const usePayspace = () => useContext(PayspaceContext);

/**
 * `PayspaceUpdate` type.
 */

type PayspaceUpdate = {
  greeting?: string;
  logo?: ImageSource;
  name?: string;
  ogImage?: ImageSource;
  signupMode?: PayspaceSignupMode;
  status?: PayspaceStatus;
  title?: string;
};

/**
 * Payspace updater context.
 */

// eslint-disable-next-line @typescript-eslint/no-extra-parens
const PayspaceUpdaterContext = createContext<(update: PayspaceUpdate) => void>(() => {});

/**
 * Export `usePayspaceUpdater`.
 */

export function usePayspaceUpdater() {
  return useContext(PayspaceUpdaterContext);
}

/**
 * `Props` type.
 */

type Props = {
  backgroundImage: ImageSources;
  backgroundOverlay: ImageSources;
  children: ReactNode;
  earlyAccessLink: string;
  imageMobile: ImageSources;
  logoLink: string;
  supportEmail: string;
};

/**
 * Export `PayspaceProvider` container.
 */

export function PayspaceProvider(props: Props): JSX.Element {
  const { backgroundImage, backgroundOverlay, children, earlyAccessLink, imageMobile, logoLink, supportEmail } = props;
  const [payspace, setPayspace] = useState<Payspace | null>(() => initialPayspaceData);
  const updatePayspace = useCallback((update: PayspaceUpdate) => {
    const { greeting, logo, name, signupMode, status, title } = update;

    setPayspace((payspace: Payspace | null) => {
      return {
        ...payspace ?? {},
        ...name ? { name } : {},
        ...greeting ? { greeting } : {},
        ...logo !== undefined ? { logo } : {},
        ...signupMode ? { signupMode } : {},
        ...status ? { status } : {},
        ...title ? { title } : {}
      } as Payspace;
    });
  }, []);

  return (
    <PayspaceUpdaterContext.Provider value={updatePayspace}>
      <PayspaceContext.Provider value={payspace}>
        {!isEmpty(payspace) ? children : (
          <PayspaceNotFound
            backgroundImage={backgroundImage}
            backgroundOverlay={backgroundOverlay}
            earlyAccessLink={earlyAccessLink}
            imageMobile={imageMobile}
            logoLink={logoLink}
            supportEmail={supportEmail}
          />
        )}
      </PayspaceContext.Provider>
    </PayspaceUpdaterContext.Provider>
  );
}
